/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LogsTab.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Popover,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";

function LogsTab({ expanded, setExpanded, logs = [], loading }) {
  const [logsDateRange, setLogsDateRange] = useState([null, null]);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);

  const compactDatePickerSx = {
    width: "120px !important",
    height: "250px !important",
    "& .MuiPickersLayout-root": {
      minWidth: "0 !important",
      maxWidth: "120px !important",
    },
    "& .MuiPickersLayout-contentWrapper": {
      padding: 0,
    },
    "& .MuiPickersCalendarHeader-switchViewButton": {
      display: "none",
    },
    "& .MuiPickersCalendarHeader-root": {
      position: "relative",
      paddingLeft: "1px",
      paddingRight: "1px",
      justifyContent: "space-between",
      minHeight: "20px",
      marginBottom: 0,
    },
    "& .MuiPickersCalendarHeader-labelContainer": {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
    },
    "& .MuiPickersCalendarHeader-label": {
      fontSize: "14px",
    },
    "& .MuiPickersArrowSwitcher-root": {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
    },
    "& .MuiPickersArrowSwitcher-spacer": {
      display: "none",
    },
    "& .MuiPickersArrowSwitcher-button": {
      padding: 0,
    },
    "& .MuiDayCalendar-header": {
      gap: "0px",
      justifyContent: "space-between",
    },
    "& .MuiDayCalendar-weekContainer": {
      margin: "0px",
      justifyContent: "space-between",
    },
    "& .MuiPickersDay-root": {
      fontSize: "12px",
      width: "70px",
      height: "30px",
      margin: "0px",
      borderRadius: "0px !important",
    },
    "& .MuiDayCalendar-weekDayLabel": {
      fontSize: "12px",
      width: "70px",
      height: "30px",
      margin: "0px",
      borderRadius: "0px !important",
    },
    "& .MuiPickersDay-root.Mui-selected": {
      backgroundColor: "#000000",
      color: "#FFFFFF",
      "&:hover": {
        backgroundColor: "#000000",
      },
      borderRadius: "0px !important",
    },
    "& .MuiPickersDay-root:not(.Mui-selected)": {
      borderRadius: "0px !important",
    },
    "& .MuiPickersYear-yearButton.Mui-selected": {
      backgroundColor: "#000000 !important",
      color: "#FFFFFF !important",
      "&:hover": {
        backgroundColor: "#000000 !important",
      },
      borderRadius: "0px !important",
    },
    "& .MuiPickersMonth-monthButton.Mui-selected": {
      backgroundColor: "#000000 !important",
      color: "#FFFFFF !important",
      "&:hover": {
        backgroundColor: "#000000 !important",
      },
      borderRadius: "0px !important",
    },
  };

  const handleAccordionToggle = (id) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  const filteredLogs = useMemo(() => {
    const [startDate, endDate] = logsDateRange;
    if (!startDate && !endDate) return logs;

    return logs?.filter((log) => {
      const logDate = moment.utc(log?.created_at).local();

      if (startDate && endDate) {
        return (
          logDate.isSameOrAfter(startDate, "day") &&
          logDate.isSameOrBefore(endDate, "day")
        );
      }

      if (startDate) return logDate.isSameOrAfter(startDate, "day");
      if (endDate) return logDate.isSameOrBefore(endDate, "day");
      return true;
    });
  }, [logsDateRange, logs]);

  return (
    <Box>
      <Box
        sx={{
          m: 0,
          mb: 2,
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          p: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "#53565A" }}>
            Select a Date
          </Typography>
          <TextField
            size="small"
            placeholder="MM/DD/YYYY — MM/DD/YYYY"
            value={
              logsDateRange[0] && logsDateRange[1]
                ? `${logsDateRange[0].format(
                    "MM/DD/YYYY",
                  )} — ${logsDateRange[1].format("MM/DD/YYYY")}`
                : ""
            }
            onClick={(e) => setDatePickerAnchor(e.currentTarget)}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {(logsDateRange[0] || logsDateRange[1]) && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLogsDateRange([null, null]);
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16, color: "#000" }} />
                    </IconButton>
                  )}
                  <CalendarMonthIcon
                    sx={{ fontSize: 18, color: "#000", cursor: "pointer" }}
                  />
                </Box>
              ),
            }}
            sx={{
              width: "235px",
              cursor: "pointer",
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                cursor: "pointer",
                "& fieldset": {
                  borderColor: "#000000",
                  borderRadius: "0px",
                },
                "&:hover fieldset": {
                  borderColor: "#000000",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />

          <Popover
            open={Boolean(datePickerAnchor)}
            anchorEl={datePickerAnchor}
            onClose={() => setDatePickerAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            slotProps={{
              paper: {
                sx: {
                  overflow: "visible",
                  borderRadius: "0px",
                },
              },
            }}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box sx={{ display: "flex", gap: 0, padding: 0 }}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={logsDateRange[0]}
                  onChange={(newValue) => {
                    setLogsDateRange([newValue, logsDateRange[1]]);
                    if (newValue && logsDateRange[1]) {
                      setDatePickerAnchor(null);
                    }
                  }}
                  maxDate={logsDateRange[1]}
                  slotProps={{
                    actionBar: { actions: [] },
                  }}
                  sx={compactDatePickerSx}
                />
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mt: "16px", mb: 0 }}
                />
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={logsDateRange[1]}
                  onChange={(newValue) => {
                    setLogsDateRange([logsDateRange[0], newValue]);
                    if (logsDateRange[0] && newValue) {
                      setDatePickerAnchor(null);
                    }
                  }}
                  minDate={logsDateRange[0]}
                  slotProps={{
                    actionBar: { actions: [] },
                  }}
                  sx={compactDatePickerSx}
                />
              </Box>
            </LocalizationProvider>
          </Popover>
        </Box>
      </Box>

      <Box
        sx={{
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pr: 1,
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "8px",
          },
        }}
      >
        {renderLogContent()}
      </Box>
    </Box>
  );

  function renderLogContent() {
    if (loading) {
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <CircularProgress size={30} />
        </Box>
      );
    }
    if (filteredLogs.length > 0) {
      return (
        <>
          {filteredLogs.map((log, idx) => (
            <Accordion
              key={log.id || `log-${log.created_at || idx}-${idx}`}
              expanded={expanded === idx}
              sx={{
                boxShadow: 3,
                borderRadius: 1,
                backgroundColor: "#f9f9f9",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                onClick={() => handleAccordionToggle(idx)}
                expandIcon={
                  expanded === idx ? (
                    <ExpandLessIcon sx={{ color: "black" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: "black" }} />
                  )
                }
                sx={{
                  backgroundColor: expanded === idx ? "#d5d7dbff" : "#fff",
                  minHeight: "48px !important",
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "start",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "black",
                    mb: 0.5,
                    textAlign: "start",
                  }}
                >
                  Timestamp
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 200,
                    color: "black",
                    mb: 0.5,
                    textAlign: "start",
                  }}
                >
                  {dayjs(log?.created_at).format("M/D/YYYY, h:mm:ss A")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: "white",
                  maxHeight: 400,
                  overflowY: "auto",
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", mb: 1 }}>
                  <Typography sx={{ fontWeight: "bold", mr: 1 }}>
                    Changed by:
                  </Typography>
                  <Typography>{log?.changed_by?.name}</Typography>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                    Changes:
                  </Typography>

                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: "#f4f4f4",
                      p: 1.5,
                      borderRadius: 1,
                      fontSize: "0.85rem",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowX: "auto",
                      maxHeight: 300,
                    }}
                  >
                    <strong>previous:</strong>{" "}
                    {typeof log?.before_changes === "object"
                      ? JSON.stringify(log.before_changes, null, 2)
                      : String(log?.before_changes || "—")}
                    <br />
                    <strong> current:</strong>{" "}
                    {typeof log?.current_changes === "object"
                      ? JSON.stringify(log.current_changes, null, 2)
                      : String(log?.current_changes || "—")}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      );
    }
    return (
      <Typography
        sx={{
          textAlign: "center",
          mt: 5,
          color: "#888",
          fontFamily: "Manrope, sans-serif",
        }}
      >
        No logs found
      </Typography>
    );
  }
}

LogsTab.propTypes = {
  expanded: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setExpanded: PropTypes.func,
  logs: PropTypes.array,
  loading: PropTypes.bool,
};

export default LogsTab;
